import { useMemo } from 'react';
import { maskPostalCode, maskTextAndNumbers, unmaskNumber } from '../../shared/utils/masks';
import { useSimuladorEnergiaSolarState } from '../state';
import { SIMULATION_STEPS } from '../simulation-steps';

const ProjectFields = {
  projectValue: {
    type: 'money',
    label: 'Valor do projeto',
    extraLabel: 'Informe o valor do projeto',
    helperText: 'Valor mínimo de R$ 500,00',
    maskFunction: (value = '', prev = '') => (`${value}`.length > 12 ? prev : value),
  },
  entryValue: {
    type: 'money',
    label: 'Valor de entrada',
    extraLabelHtml: true,
    extraLabel: 'Vai dar algum valor de entrada? Informe abaixo <small>(Não obrigatório)</small>',
    maskFunction: (value = '', prev = '') => (`${value}`.length > 12 ? prev : value),
  },
  store: {
    type: 'select',
    options: [],
    label: 'Loja',
    extraLabel: 'Busque a loja por nome, endereço ou CNPJ',
    allowClear: true,
  },
};

const WithoutProjectFields = {
  roofType: {
    type: 'select',
    label: 'Tipo de telhado',
    options: [],
  },
};

const AddressFields = {
  address: {
    label: 'Rua',
    maskFunction: maskTextAndNumbers,
  },
  addressNumber: {
    label: 'Número',
    maskFunction: unmaskNumber,
    inputMode: 'numeric',
  },
  complement: {
    label: 'Complemento (Apartamento, bloco, casa)',
    hideLabelOverflow: true,
    maskFunction: maskTextAndNumbers,
  },
  neighborhood: {
    label: 'Bairro',
    maskFunction: maskTextAndNumbers,
  },
  city: {
    label: 'Cidade',
    disabled: true,
  },
  state: {
    label: 'Estado',
    disabled: true,
  },
};

export function useInitialDataFields({
  handleCepChange,
  showAddressFields,
  addressCompleted,
  isNoAddressNumberChecked,
  currentRoofType,
  currentSearchStore,
  setFieldValue,
}) {
  const { hasSelectedType, simulationStep, simulationType, roofOptions } =
    useSimuladorEnergiaSolarState();

  const fields = useMemo(() => {
    if (!hasSelectedType) {
      return [];
    }

    let fieldDefinition = {};

    if (simulationStep?.id === SIMULATION_STEPS.FORM_USAGE.id) {
      return [
        {
          name: 'averageUsage',
          label: 'Média de gasto',
          type: 'money',
          helperText: 'Valor mínimo de R$ 200,00',
          maskFunction: (value = '', prev = '') => (`${value}`.length > 9 ? prev : value),
        },
      ];
    }

    if (simulationType === 'existing') {
      fieldDefinition = ProjectFields;
    }

    if (simulationType === 'new') {
      const roofs = roofOptions || [];

      fieldDefinition = {
        roofType: {
          ...WithoutProjectFields.roofType,
          options: roofs,
          extraLabelHtml: roofs.length > 0,
          extraLabel:
            roofs.length > 0
              ? `Qual o tipo de telhado da sua casa?<br /><small>${roofs.map((r) => `${r.label}: ${r.example}`).join(' - ')}</small>`
              : 'Qual o tipo de telhado da sua casa?',
        },
      };

      if (!!currentRoofType) {
        fieldDefinition = {
          ...fieldDefinition,
          postalCode: {
            label: 'CEP',
            maskFunction: maskPostalCode,
            inputMode: 'numeric',
            onBlur: (ev) => handleCepChange(ev.target.value),
            extraLabel: showAddressFields ? 'Informe os dados do seu endereço' : 'Informe seu CEP',
          },
        };

        if (showAddressFields) {
          fieldDefinition = {
            ...fieldDefinition,
            ...AddressFields,
          };

          if (fieldDefinition.addressNumber) {
            fieldDefinition.addressNumber.disabled = isNoAddressNumberChecked;
          }
        }

        if (addressCompleted) {
          fieldDefinition = {
            roofType: fieldDefinition.roofType,
            searchStore: {
              maskFunction: maskPostalCode,
              label: 'Buscar',
              extraLabel: 'Parceiros próximo de você',
              autocomplete: 'off',
              'aria-autocomplete': 'none',
              startAdornment: !!currentSearchStore ? null : (
                <img src="assets/simulador-energia-solar/jornada/ic-search.svg" alt="buscar" />
              ),
              endAdornment: !currentSearchStore ? null : (
                <img
                  src="assets/simulador-energia-solar/jornada/ic-clear.svg"
                  alt="limpar"
                  role="button"
                  onClick={(ev) => {
                    ev.preventDefault();
                    setFieldValue('searchStore', '');
                  }}
                />
              ),
            },
          };
        }
      }
    }

    return Object.entries(fieldDefinition).map(([name, value]) => ({ ...value, name }));
  }, [
    hasSelectedType,
    simulationStep?.id,
    simulationType,
    roofOptions,
    currentRoofType,
    showAddressFields,
    addressCompleted,
    handleCepChange,
    isNoAddressNumberChecked,
    currentSearchStore,
    setFieldValue,
  ]);

  return fields;
}
